import React, {
  useCallback,
  useState,
  createContext,
  useEffect,
  useMemo,
  useRef,
  useContext,
} from 'react';
import { Amplify } from 'aws-amplify';
import { generateClient } from 'aws-amplify/api';
import {
  Notification,
  NotificationStatus,
  SetNotificationsRequest,
} from '@/types/services/notification';
import { notificationService } from '@/services/notification';
import { useAppSelector, useAppDispatch } from '@/store';
import userThunks from '@/store/thunks/user';
import { authSelector } from '@/store/reducers/auth';
import userUtils from '@/utils/user';
import { formatDateShort } from '@/utils/formatters';
import { TFunction } from 'i18next';
import config from '@/config';
import commonUtils from '@/utils/common';
import store from '@/store';
import { refreshTokens } from '@/services/tokenExchange';
import { EventDispatcherContext, DispatcherEvents } from './eventDispatcherContext';

Amplify.configure({
  API: {
    GraphQL: {
      endpoint: config.notificationsQlUrl,
      region: 'eu-west-1',
      defaultAuthMode: 'lambda',
    },
  },
});

export type NormalizeNotification = Notification & {
  createdAtDate: Date;
  dataObj: any;
  localazyEventType: string;
};

export const notificationSpecifics: {
  [eventType: string]: {
    showPopup?: boolean;
    needsUpdatedUserData?: boolean;
    showActionText?: boolean;
  };
} = {
  levelChanged: {
    showPopup: true,
  },
  dealExpired: {
    showPopup: true,
  },
  accountDeleted: {
    showPopup: true,
  },
  studentGroupAllowIndividualAdvancement: {
    showPopup: true,
    needsUpdatedUserData: true,
  },
  studentGroupPlacementTestRequired: {
    showPopup: true,
  },
  movedToDifferentClassWithPlacementTestRequired: {
    showPopup: true,
  },
  movedToDifferentClassWithLevelSet: {
    showPopup: true,
  },
  studentGroupLevelSet: {
    showPopup: true,
  },
  movedToDifferentClass: {
    needsUpdatedUserData: true,
  },
  nameChanged: {
    needsUpdatedUserData: true,
  },
  interfaceLanguageChanged: {
    needsUpdatedUserData: true,
  },
  assignedToNewClass: {
    showActionText: true,
  },
  coTeacherAssignedToNewClass: {
    showActionText: true,
  },
  studentsAssignedToClassByAdmin: {
    showActionText: true,
  },
  studentsJoinedViaTeacherInvitation: {
    showActionText: true,
  },
  licenseLimitReached: {
    showActionText: true,
  },
};

export const getNotificationText = (notification: NormalizeNotification | null, t: TFunction) => {
  let title: string = '';
  let body: string = '';
  let actionText: string = '';
  let sendData: string = '';

  if (notification) {
    const obj = {
      ...notification.dataObj,
    };

    if ('date' in obj) {
      obj.dateStr = formatDateShort(
        obj.date.toUpperCase().endsWith('Z') ? obj.date : `${obj.date}Z`
      );
    }
    if ('oldLevelId' in obj) {
      obj.oldLevel = obj.oldLevelId > 0 ? t(`levelName_${obj.oldLevelId}`) : '';
    }
    if ('newLevelId' in obj) {
      obj.newLevel = obj.newLevelId > 0 ? t(`levelName_${obj.newLevelId}`) : '';
    }
    if ('newLanguage' in obj) {
      obj.newLanguageStr = t(`languages.${obj.newLanguage}`);
    }
    if ('oldGradeId' in obj) {
      obj.oldGradeStr = t(`grade_${obj.oldGradeId}`);
    }
    if ('newGradeId' in obj) {
      obj.newGradeStr = t(`grade_${obj.newGradeId}`);
    }
    if ('skillsList' in obj) {
      obj.skillsListStr = (obj.skillsList || [])
        .map((skillId: string) => t(`skills.${(skillId || '').toLowerCase()}`))
        .join(', ');
    }

    if (notification.createdAt) {
      obj.createdAtStr = formatDateShort(
        notification.createdAt.toUpperCase().endsWith('Z')
          ? notification.createdAt
          : `${notification.createdAt}Z`
      );
    }

    title = t(`notifications.event.${notification.localazyEventType}.title`, obj) + '';
    body = t(`notifications.event.${notification.localazyEventType}.body`, obj) + '';
    sendData =
      'operatorFirstName' in obj && obj.operatorFirstName
        ? t(`notifications.list.sendDataWithOperator`, obj) + ''
        : t(`notifications.list.sendDataWithoutOperator`, obj) + '';

    if (notificationSpecifics[notification.eventType]?.showActionText) {
      actionText = t(`notifications.event.${notification.localazyEventType}.actionText`, obj) + '';
    }

    // use singular form
    switch (notification.localazyEventType) {
      case 'classesDeleted':
        if (`${obj.numberOfClasses}` === '1') {
          body = t(`notifications.event.${notification.localazyEventType}.body_one`, obj) + '';
        }
        break;
      case 'topicCompletedLowScore':
        if ((obj.skillsList || []).length === 1) {
          body = t(`notifications.event.${notification.localazyEventType}.body_one`, obj) + '';
        }
        break;
      case 'studentsAssignedToClassByAdmin':
      case 'studentsDeleted':
      case 'studentsJoinedSinceLastLoginWithStudents':
      case 'studentsJoinedViaTeacherInvitation':
      case 'studentsMovedOutOfTheClass':
      case 'teacherDeletedClassWithStudents':
      case 'studentInactivityDigest2w':
      case 'studentInactivityDigest1m':
        if (`${obj.numberOfStudents}` === '1') {
          body = t(`notifications.event.${notification.localazyEventType}.body_one`, obj) + '';
        }
        break;
      case 'studentWeeklyInactivity':
        if ((`${obj.lastCompletedActivityScore || ''}`.trim() || '0') === '0') {
          body = t(`notifications.event.${notification.localazyEventType}.body_noScore`, obj) + '';
        }
        break;
    }
  }

  return {
    title,
    body,
    actionText,
    sendData,
  };
};

interface NotificationsContextProps {
  notifications: NormalizeNotification[];
  numberOfUnreadNotifications: number;
  setNotificationStatus: (notificationsStatus: SetNotificationsRequest[]) => void;
  setAllAsRead: () => void;
  showPopup: boolean;
  setShowPopup: (show: boolean) => void;
  popupNotification?: NormalizeNotification | null;
}

export const NotificationsContext = createContext<NotificationsContextProps>({
  notifications: [],
  numberOfUnreadNotifications: 0,
  setNotificationStatus: () => {},
  setAllAsRead: () => {},
  showPopup: false,
  setShowPopup: () => {},
  popupNotification: null,
});

export const NotificationsProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAppSelector(authSelector);
  const appDispatch = useAppDispatch();
  const eventDispatcher = useContext(EventDispatcherContext);
  const [currentToken, setCurrentToken] = useState(localStorage.getItem('token'));
  const [notifications, setNotifications] = useState<NormalizeNotification[]>([]);
  const [showNotifications, setShowNotifications] = useState<NormalizeNotification[]>([]);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [popupNotification, setPopupNotification] = useState<NormalizeNotification | null>(null);
  const isActive = useRef<boolean>(true);

  const numberOfUnreadNotifications = useMemo(() => {
    return showNotifications.filter(n => n.status === NotificationStatus.Unread).length;
  }, [showNotifications]);

  const normalizeNotification = useCallback(
    (notification: Notification) => {
      let dataObj: any = {};
      try {
        dataObj =
          typeof notification.data === 'string'
            ? JSON.parse(notification.data || '{}')
            : notification.data || {};
      } catch (e) {}

      const createdAtDate = new Date(notification.createdAt);
      const status = notification.status || NotificationStatus.Unread;
      let localazyEventType = notification.eventType;

      switch (notification.eventType) {
        case 'dealExpired':
          localazyEventType = userUtils.isAdmin(user)
            ? 'adminDealExpired'
            : userUtils.isTeacher(user)
              ? 'teacherDealExpired'
              : 'studentDealExpired';
          break;
        case 'weeklyClassSummary':
          localazyEventType = dataObj.thereActions
            ? 'weeklyClassSummaryWithActivity'
            : 'weeklyClassSummaryWithOutActivity';
          break;
        case 'weeklySchoolSummary':
          localazyEventType = dataObj.thereActions
            ? 'weeklySchoolSummaryWithActivity'
            : 'weeklySchoolSummaryWithOutActivity';
          break;
        case 'teacherDeletedClass':
          localazyEventType = dataObj.hasStudents
            ? 'teacherDeletedClassWithStudents'
            : 'teacherDeletedClassWithOutStudents';
          break;
        case 'studentsJoinedSinceLastLogin':
          localazyEventType = dataObj.hasStudents
            ? 'studentsJoinedSinceLastLoginWithStudents'
            : 'studentsJoinedSinceLastLoginWithOutStudents';
          break;
        case 'studentInactivityDigest':
          localazyEventType = `${notification.eventType}${dataObj.period || ''}`;
          break;
      }

      return {
        ...notification,
        dataObj,
        createdAtDate,
        status,
        localazyEventType,
      } as NormalizeNotification;
    },
    [user]
  );

  const notificationStatusUpdates = useCallback((data: SetNotificationsRequest[]) => {
    const statusMapper = data.reduce(
      (acc, _item) => {
        const item = _item as any;
        const itemStatus = item.status || item.Status;
        const itemNotificationId = item.notificationId || item.NotificationId;

        if (!itemNotificationId || (!itemStatus && itemStatus !== 0)) {
          return acc;
        }
        const status =
          itemStatus === 0
            ? NotificationStatus.Read
            : itemStatus === 1
              ? NotificationStatus.Unread
              : itemStatus === 2
                ? NotificationStatus.Deleted
                : itemStatus;
        acc[itemNotificationId] = status as NotificationStatus;
        return acc;
      },
      {} as {
        [notificationId: string]: NotificationStatus;
      }
    );

    setNotifications(prevState => {
      return prevState.map(n => {
        if (statusMapper[n.notificationId]) {
          return { ...n, status: statusMapper[n.notificationId] };
        }
        return n;
      });
    });
  }, []);

  const readAllNotifications = useCallback(() => {
    setNotifications(prevState => {
      return prevState.map(n => {
        if (n.status === NotificationStatus.Unread) {
          return { ...n, status: NotificationStatus.Read };
        }
        return n;
      });
    });
  }, []);

  useEffect(() => {
    setShowNotifications(
      notifications
        .filter(n => n.status !== NotificationStatus.Deleted)
        .sort((n1, n2) => {
          const dateSort =
            (n2.createdAtDate as Date).getTime() - (n1.createdAtDate as Date).getTime();

          const n1HasActionText = notificationSpecifics[n1.eventType]?.showActionText || false;
          const n2HasActionText = notificationSpecifics[n2.eventType]?.showActionText || false;

          if (n1HasActionText === n2HasActionText) {
            return dateSort;
          }

          return n1HasActionText ? -1 : 1;
        })
        .slice(0, 50)
    );
  }, [notifications]);

  useEffect(() => {
    const userId = user?.metadata.id || '';
    isActive.current = true;

    if (!userId) {
      setNotifications([]);
      return;
    }

    const init = async () => {
      let notifications: NormalizeNotification[] = [];

      try {
        const notificationsResponse = await notificationService.getNotifications(userId);
        notifications = (notificationsResponse?.data?.data || []).map(n =>
          normalizeNotification(n)
        );
      } catch (e) {}

      if (!isActive.current) {
        return;
      }

      setNotifications(notifications);
    };

    init();

    return () => {
      isActive.current = false;
    };
  }, [user?.metadata.id, normalizeNotification]);

  useEffect(() => {
    const userId = user?.metadata.id || '';
    const token = localStorage.getItem('token') || currentToken || '';
    setCurrentToken(token);

    if (!userId) {
      return;
    }

    const refreshTokensAsync = async () => {
      const newToken = await refreshTokens(store.dispatch);
      if (newToken) {
        localStorage.setItem('token', newToken);
        if (isActive.current) {
          setCurrentToken(newToken);
        }
      }
    };
    if (token && commonUtils.shouldTokenRefresh(token)) {
      refreshTokensAsync();
    }

    const notificationClient = generateClient();

    const graphqlClient = notificationClient.graphql({
      query: `subscription OnNewNotification($userId: ID!) {
                onNewNotification(userId: $userId) {
                    notificationId 
                    eventType 
                    data 
                    createdAt
                    userId
                }
            }`,
      variables: {
        userId: userId,
      },
      authToken: `Bearer ${token}`,
    }) as any;

    const sub = graphqlClient.subscribe({
      next: (data: any) => {
        const newNotification = data.data.onNewNotification;
        const newNormalizeNotification = normalizeNotification(newNotification);
        // console.log('New notification received:', newNormalizeNotification);

        if (newNormalizeNotification.eventType === 'notificationStatusUpdates') {
          const dataObj = (newNormalizeNotification.dataObj || []) as SetNotificationsRequest[];
          notificationStatusUpdates(dataObj);
          return;
        }

        if (newNormalizeNotification.eventType === 'readAllNotifications') {
          readAllNotifications();
          return;
        }

        setNotifications(prevState => {
          return [newNormalizeNotification, ...prevState];
        });

        if (notificationSpecifics[newNormalizeNotification.eventType]?.showPopup) {
          setShowPopup(true);
          setPopupNotification(newNormalizeNotification);
        }

        if (notificationSpecifics[newNormalizeNotification.eventType]?.needsUpdatedUserData) {
          appDispatch(userThunks.userInfo({ retryCount: 1 }));
          eventDispatcher.dispatch(DispatcherEvents.USER_DATA_CHANGE, null);
        }
      },
      error: (err: any) => {
        console.error('Notification WS Error:', err);
        setCurrentToken(localStorage.getItem('token'));
      },
    });

    return () => {
      try {
        sub.unsubscribe();
      } catch (e) {}
    };
  }, [
    user?.metadata.id,
    currentToken,
    normalizeNotification,
    notificationStatusUpdates,
    readAllNotifications,
    eventDispatcher,
  ]);

  const setNotificationStatus = useCallback(
    (notificationsStatus: SetNotificationsRequest[]) => {
      const userId = user?.metadata.id || '';

      if (!userId) {
        return;
      }

      notificationService.setNotificationsStatus(userId, notificationsStatus);
      notificationStatusUpdates(notificationsStatus);
    },
    [user, notificationStatusUpdates]
  );

  const setAllAsRead = useCallback(() => {
    const userId = user?.metadata.id || '';

    if (!userId) {
      return;
    }

    notificationService.setReadAll(userId);
    readAllNotifications();
  }, [user, readAllNotifications]);

  return (
    <NotificationsContext.Provider
      value={{
        notifications: showNotifications,
        numberOfUnreadNotifications: numberOfUnreadNotifications,
        setNotificationStatus: setNotificationStatus,
        setAllAsRead: setAllAsRead,
        showPopup: showPopup,
        setShowPopup: setShowPopup,
        popupNotification: popupNotification,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
